import React, { useEffect, useState } from 'react';
import Header from '@/components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Home, FileText, Clock, CheckCircle, XCircle, Pencil, Trash2, Eye, ArrowLeft, Search } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { getBrokerProperties, getBrokerPropertyStats, deleteProperty } from '@/services/property';
import { PaginationControl } from '@/components/shared/PaginationControl';
import { Input } from '@/components/ui/input';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Badge } from '@/components/ui/badge';
import { toast } from '@/hooks/use-toast';
import { PropertyType } from '@/types/property';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { encodeS3Url } from '@/components/encodeS3Url';
import { capitalizeWords } from '@/helper';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { pagination } from '@/constants';

const BrokerProperties = () => {
    const [properties, setProperties] = useState<PropertyType[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [stats, setStats] = useState({
        totalProperties: 0,
        pending: 0,
        published: 0,
        rejected: 0,
        draft: 0
    });
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalProperties, setTotalProperties] = useState(0);

    const [statusFilter, setStatusFilter] = useState('ALL');
    const [deleteId, setDeleteId] = useState<string | null>(null);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

    const fetchStats = async () => {
        try {
            const statsData = await getBrokerPropertyStats();
            setStats(statsData);
        } catch (error) {
            console.error("Error fetching stats:", error);
        }
    };

    const fetchProperties = async (page = 1) => {
        setIsLoading(true);
        try {
            const params = {
                page,
                limit: pagination.limit,
                searchQuery: searchQuery,
                approvalStatus: statusFilter !== 'ALL' ? statusFilter : undefined
            };

            const { data, paginationData } = await getBrokerProperties(params);

            if (Array.isArray(data)) {
                setProperties(data);
                setTotalPages(paginationData?.totalPages || 1);
                setTotalProperties(paginationData?.totalProperties || 0);
                setCurrentPage(page);
            } else {
                setProperties([]);
                setTotalPages(1);
                setTotalProperties(0);
                setCurrentPage(1);
            }

        } catch (error) {
            console.error("Error fetching data:", error);
            toast({
                title: "Error",
                description: "Failed to fetch properties data.",
                variant: "destructive"
            });
            setProperties([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchStats();
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchProperties(1);
        }, 500); // Debounce search
        return () => clearTimeout(timer);
    }, [searchQuery, statusFilter]);

    const handlePageChange = (page: number) => {
        fetchProperties(page);
    };

    const handleDelete = (id: string) => {
        setDeleteId(id);
        setIsDeleteDialogOpen(true);
    };

    const clearFilters = () => {
        setSearchQuery('');
        setStatusFilter('ALL');
    };

    const confirmDelete = async () => {
        if (!deleteId) return;

        try {
            await deleteProperty(deleteId);
            toast({
                title: "Success",
                description: "Property deleted successfully.",
            });
            toast({
                title: "Success",
                description: "Property deleted successfully.",
            });
            fetchProperties(currentPage); // Refresh data
            fetchStats(); // Refresh stats
        } catch (error) {
            console.error("Error deleting property:", error);
            toast({
                title: "Error",
                description: "Failed to delete property.",
                variant: "destructive"
            });
        } finally {
            setIsDeleteDialogOpen(false);
            setDeleteId(null);
        }
    };

    const formatPrice = (amount: number, currency: string) => {
        return new Intl.NumberFormat('en-AE', {
            style: 'currency',
            currency: currency,
            maximumFractionDigits: 0,
        }).format(amount);
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'PUBLISHED':
                return <Badge className="bg-green-100 text-green-800 hover:bg-green-100 hover:text-green-800">Published</Badge>;
            case 'PENDING':
                return <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100 hover:text-yellow-800">Pending</Badge>;
            case 'DRAFT':
                return <Badge className="bg-gray-100 text-gray-800 hover:bg-gray-100 hover:text-gray-800">Draft</Badge>;
            case 'REJECTED':
                return <Badge className="bg-red-100 text-red-800 hover:bg-red-100 hover:text-red-800">Rejected</Badge>;
            default:
                return <Badge variant="outline">{status}</Badge>;
        }
    };




    return (
        <div className="min-h-screen flex flex-col">
            <Header />
            <div className="pt-32 pb-20 bg-secondary/30 flex-1">
                <div className="container mx-auto px-4 md:px-6">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
                        <div>
                            <h1 className="text-3xl font-bold">My Properties</h1>
                            <p className="text-muted-foreground mt-1">Manage your property listings</p>
                        </div>
                        <Button asChild variant="outline">
                            <Link to="/broker/dashboard">
                                <ArrowLeft className="mr-2 h-4 w-4" />
                                Back to Dashboard
                            </Link>
                        </Button>
                    </div>

                    {/* Stats Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
                        {isLoading ? (
                            [1, 2, 3, 4, 5].map((i) => (
                                <Card key={i}>
                                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                        <Skeleton className="h-4 w-20" />
                                        <Skeleton className="h-4 w-4 rounded-full" />
                                    </CardHeader>
                                    <CardContent>
                                        <Skeleton className="h-8 w-12" />
                                    </CardContent>
                                </Card>
                            ))
                        ) : (
                            <>
                                <Card>
                                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                        <CardTitle className="text-sm font-medium">Total Properties</CardTitle>
                                        <Home className="h-4 w-4 text-muted-foreground" />
                                    </CardHeader>
                                    <CardContent>
                                        <div className="text-2xl font-bold">{stats.totalProperties}</div>
                                    </CardContent>
                                </Card>
                                <Card>
                                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                        <CardTitle className="text-sm font-medium">Published</CardTitle>
                                        <FileText className="h-4 w-4 text-muted-foreground" />
                                    </CardHeader>
                                    <CardContent>
                                        <div className="text-2xl font-bold">{stats.published}</div>
                                    </CardContent>
                                </Card>
                                <Card>
                                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                        <CardTitle className="text-sm font-medium">Draft</CardTitle>
                                        <Home className="h-4 w-4 text-muted-foreground" />
                                    </CardHeader>
                                    <CardContent>
                                        <div className="text-2xl font-bold">{stats.draft}</div>
                                    </CardContent>
                                </Card>
                                <Card>
                                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                        <CardTitle className="text-sm font-medium">Pending</CardTitle>
                                        <Clock className="h-4 w-4 text-muted-foreground" />
                                    </CardHeader>
                                    <CardContent>
                                        <div className="text-2xl font-bold">{stats.pending}</div>
                                    </CardContent>
                                </Card>
                                <Card>
                                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                        <CardTitle className="text-sm font-medium">Rejected</CardTitle>
                                        <XCircle className="h-4 w-4 text-muted-foreground" />
                                    </CardHeader>
                                    <CardContent>
                                        <div className="text-2xl font-bold">{stats.rejected}</div>
                                    </CardContent>
                                </Card>
                            </>
                        )}
                    </div>


                    <Card className="mb-8">
                        <CardHeader className="pb-3">
                            <CardTitle>Filters</CardTitle>
                            <CardDescription>Search and filter properties</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="flex flex-col md:flex-row gap-4">
                                <div className="relative flex-1">
                                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        type="text"
                                        placeholder="Search by title, location..."
                                        className="pl-8"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                    />
                                </div>
                                <Select value={statusFilter} onValueChange={setStatusFilter}>
                                    <SelectTrigger className="w-full md:w-[200px]">
                                        <SelectValue placeholder="Filter by status" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="ALL">All Status</SelectItem>
                                        <SelectItem value="PUBLISHED">Published</SelectItem>
                                        <SelectItem value="PENDING">Pending</SelectItem>
                                        <SelectItem value="REJECTED">Rejected</SelectItem>
                                        <SelectItem value="DRAFT">Draft</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </CardContent>
                        <CardFooter>
                            <div className="flex justify-between w-full">
                                <Button variant="outline" onClick={clearFilters}>
                                    Clear Filters
                                </Button>
                                <div className="text-sm text-muted-foreground">
                                    Showing {properties.length} of {totalProperties} properties
                                </div>
                            </div>
                        </CardFooter>
                    </Card>

                    <Card>
                        <CardContent>
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Image</TableHead>
                                        <TableHead>Property Title</TableHead>
                                        <TableHead>Address</TableHead>
                                        <TableHead>Price</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead className="text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {isLoading ? (
                                        [1, 2, 3, 4, 5].map((i) => (
                                            <TableRow key={i}>
                                                <TableCell><Skeleton className="h-12 w-16 rounded-md" /></TableCell>
                                                <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                                                <TableCell><Skeleton className="h-4 w-40" /></TableCell>
                                                <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                                                <TableCell><Skeleton className="h-6 w-20 rounded-full" /></TableCell>
                                                <TableCell className="text-right"><Skeleton className="h-8 w-24 ml-auto" /></TableCell>
                                            </TableRow>
                                        ))
                                    ) : properties.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                                                No properties found.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        properties.map((property) => (
                                            <TableRow key={property.slug}>
                                                <TableCell>
                                                    <div className="h-12 w-16 rounded-md overflow-hidden bg-muted">
                                                        {property.media?.images?.[0] ? (
                                                            <img
                                                                src={encodeS3Url(property.media.images[0])}
                                                                alt={property.specifications?.titleDeed || 'Property'}
                                                                className="h-full w-full object-cover"
                                                            />
                                                        ) : (
                                                            <div className="h-full w-full flex items-center justify-center text-muted-foreground">
                                                                <Home className="h-4 w-4" />
                                                            </div>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                <TableCell className="font-medium">{capitalizeWords(property?.specifications?.titleDeed || '')}</TableCell>
                                                <TableCell>{property.location?.address || 'N/A'}</TableCell>
                                                <TableCell>{formatPrice(property.pricing?.amount || 0, property.pricing?.currency || 'AED')}</TableCell>
                                                <TableCell>{getStatusBadge(property.status)}</TableCell>
                                                <TableCell className="text-right">
                                                    <div className="flex justify-end gap-2">
                                                        <Button variant="ghost" size="icon" asChild>
                                                            <Link to={`/properties/${property.slug}`}>
                                                                <Eye className="h-4 w-4" />
                                                            </Link>
                                                        </Button>
                                                        <Button variant="ghost" size="icon" asChild>
                                                            <Link to={`/broker/property-edit/${property._id}`}>
                                                                <Pencil className="h-4 w-4" />
                                                            </Link>
                                                        </Button>
                                                        <Button variant="ghost" size="icon" onClick={() => handleDelete(property._id)}>
                                                            <Trash2 className="h-4 w-4 text-red-500" />
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </CardContent>
                        <div className="p-4 border-t flex justify-center">
                            <div className="w-auto">
                                <PaginationControl
                                    totalPages={totalPages}
                                    initialPage={currentPage}
                                    onPageChange={handlePageChange}
                                />
                            </div>
                        </div>
                    </Card>
                </div>
            </div>

            <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This action cannot be undone. This will permanently delete the property listing.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div >
    );
};

export default BrokerProperties;
